import React, { useRef, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface MarkerTooltipProps {
  locationName: string | null;
  position: { x: number; y: number } | null;
  isVisible: boolean;
  dayNumber?: number; // Add this prop
}

export const MarkerTooltip: React.FC<MarkerTooltipProps> = ({ 
  locationName, 
  position, 
  isVisible,
  dayNumber 
}) => {
  
  // Add a ref to measure tooltip width
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipWidth, setTooltipWidth] = useState(0);
  
  // Measure tooltip width when visible
  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      setTooltipWidth(tooltipRef.current.offsetWidth);
    }
  }, [isVisible, locationName, dayNumber]);

  // Early return if no position or name
  if (!position || !locationName) return null;

  // Format the display text based on whether dayNumber is available
  const displayText = dayNumber ? `Day ${dayNumber}: ${locationName}` : locationName;

  return (
    <AnimatePresence>
      {isVisible && (
        // Rest of component remains the same
        <motion.div
          ref={tooltipRef}
          className="absolute pointer-events-none z-[5000]"
          style={{
            left: tooltipWidth ? position.x - (tooltipWidth / 2) : position.x,
            top: position.y - 115,
          }}
          initial={{ opacity: 0, y: 10, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 5, scale: 0.9 }}
          transition={{ 
            duration: 0.2,
            type: "spring",
            stiffness: 500,
            damping: 30
          }}
        >
          <div className="bg-gray-900/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-md shadow-lg text-sm whitespace-nowrap font-medium border border-gray-700/30">
            {displayText}
            {/* Tooltip arrow pointing down */}
            <div 
              className="absolute w-2 h-2 bg-gray-900 transform rotate-45"
              style={{
                bottom: '-4px',
                left: '50%',
                marginLeft: '-4px',
                borderRight: '1px solid rgba(55, 65, 81, 0.3)',
                borderBottom: '1px solid rgba(55, 65, 81, 0.3)'
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}